<template>
  <b-modal
    v-model="show"
    :title="`HISTORIAL ANUAL DE DÍAS LIBRES: ${conductor ? conductor.empleado : ''}`"
    modal-class="modal-primary"
    size="xl"
    hide-footer
    no-close-on-backdrop
    scrollable
    @hidden="onHidden"
  >
    <b-overlay :show="loading" rounded="sm">
      <div v-if="conductor" class="mb-2">
        <b-row class="align-items-center">
          <b-col md="6">
            <h5 class="mb-50 font-weight-bold text-primary">
              {{ conductor.empleado }}
            </h5>
            <div class="text-muted small">
              Documento: <strong>{{ conductor.documento }}</strong> ·
              Régimen: <b-badge variant="light-primary">{{ conductor.regimen || 'SIN RÉGIMEN' }}</b-badge> ·
              Sueldo: <strong>S/. {{ formatCurrency(conductor.sueldo) }}</strong>
              <span v-if="conductor.doble_planilla">
                · Adicional: <strong>S/. {{ formatCurrency(conductor.sueldo_adicional) }}</strong>
              </span>
            </div>
          </b-col>
          <b-col md="3">
            <b-form-group label="Año a consultar:" label-class="small font-weight-bold" class="mb-0">
              <b-form-select
                v-model="anio"
                size="sm"
                :options="aniosOptions"
                @change="cargarHistorial"
              />
            </b-form-group>
          </b-col>
          <b-col md="3" class="text-right">
            <div class="p-1 rounded bg-light-primary border-primary">
              <span class="small font-weight-bold text-muted d-block">SALDO ACUMULADO</span>
              <h4 class="mb-0 font-weight-bolder" :class="saldoActual < 0 ? 'text-danger' : 'text-success'">
                {{ saldoActual }} días
              </h4>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        small
        responsive
        striped
        hover
        :items="meses"
        :fields="tableFields"
        class="font-small-3 text-nowrap"
      >
        <template #cell(mes)="data">
          <strong>{{ data.item.mes }}</strong>
          <b-badge v-if="data.item.cerrado" variant="light-secondary" class="ml-50">
            CERRADO
          </b-badge>
        </template>

        <template #cell(dias)="data">
          <span class="font-weight-bold">{{ data.value }}</span>
        </template>

        <template #cell(tomados)="data">
          <b-badge v-if="data.value > 0" variant="light-primary">{{ data.value }}</b-badge>
          <span v-else class="text-muted">0</span>
        </template>

        <template #cell(vendidos)="data">
          <b-badge v-if="data.value > 0" variant="light-success">{{ data.value }}</b-badge>
          <span v-else class="text-muted">0</span>
        </template>

        <template #cell(perdidos)="data">
          <b-badge v-if="data.value > 0" variant="light-danger">{{ data.value }}</b-badge>
          <span v-else class="text-muted">0</span>
        </template>

        <template #cell(ajustes)="data">
          <span v-if="data.value !== 0" :class="data.value > 0 ? 'text-success' : 'text-danger'">
            {{ data.value > 0 ? '+' + data.value : data.value }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(saldo)="data">
          <strong v-if="data.value !== null" :class="data.value < 0 ? 'text-danger' : 'text-success'">
            {{ data.value }} días
          </strong>
          <span v-else class="text-muted small">Futuro</span>
        </template>
      </b-table>

      <!-- Totales -->
      <div v-if="totales" class="d-flex justify-content-between align-items-center p-1 rounded bg-light mt-1">
        <span class="font-weight-bold">TOTALES ACUMULADOS {{ anio }}:</span>
        <div>
          <span class="mr-2">Meta: <strong>{{ totales.dias }}</strong></span>
          <span class="mr-2 text-primary">Tomados: <strong>{{ totales.tomados }}</strong></span>
          <span class="mr-2 text-success">Vendidos: <strong>{{ totales.vendidos }}</strong></span>
          <span class="mr-2 text-danger">Perdidos: <strong>{{ totales.perdidos }}</strong></span>
          <span>Ajustes: <strong>{{ totales.ajustes }}</strong></span>
        </div>
      </div>

      <!-- Movimientos Detallados -->
      <div v-if="movimientos.length > 0" class="mt-2">
        <h6 class="font-weight-bold mb-1">
          <feather-icon icon="ListIcon" class="mr-50" /> Movimientos detallados del año {{ anio }}:
        </h6>
        <div class="table-responsive">
          <table class="table table-sm font-small-2">
            <thead>
              <tr class="text-muted">
                <th>FECHA</th>
                <th>TIPO</th>
                <th>CANTIDAD</th>
                <th>DETALLE / MOTIVO</th>
                <th>ESTADO</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(mov, idx) in movimientos" :key="idx">
                <td>{{ mov.fecha }}</td>
                <td>
                  <b-badge :variant="getMovVariant(mov.tipo)">{{ mov.tipo.toUpperCase() }}</b-badge>
                </td>
                <td class="font-weight-bold">{{ mov.cantidad }} día(s)</td>
                <td>{{ mov.detalle || mov.motivo || '-' }}</td>
                <td>
                  <b-badge v-if="mov.estado" :variant="mov.estado === 'aprobada' ? 'light-success' : 'light-warning'">
                    {{ mov.estado }}
                  </b-badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import DiasLibresService from "../service/dias-libres.service";

export default {
  name: "HistorialDiasLibresModal",
  data() {
    return {
      show: false,
      loading: false,
      conductor: null,
      anio: new Date().getFullYear(),
      aniosOptions: [2024, 2025, 2026, 2027],
      meses: [],
      totales: null,
      saldoActual: 0,
      movimientos: [],
      tableFields: [
        { key: "mes", label: "MES" },
        { key: "dias", label: "DÍAS META" },
        { key: "tomados", label: "TOMADOS" },
        { key: "vendidos", label: "VENDIDOS" },
        { key: "perdidos", label: "PERDIDOS" },
        { key: "ajustes", label: "AJUSTES" },
        { key: "saldo", label: "SALDO ACUMULADO" },
      ],
    };
  },
  methods: {
    open(conductor, anioActual) {
      this.conductor = conductor;
      if (anioActual) this.anio = Number(anioActual);
      this.show = true;
      this.cargarHistorial();
    },
    async cargarHistorial() {
      if (!this.conductor) return;
      this.loading = true;
      try {
        const { data } = await DiasLibresService.getHistorial({
          personal_id: this.conductor.personal_id,
          anio: this.anio,
        });
        this.meses = data.meses || [];
        this.totales = data.total || null;
        this.saldoActual = data.saldo_actual || 0;
        this.movimientos = data.movimientos || [];
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "No se pudo obtener el historial del conductor.",
            icon: "AlertCircleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(val) {
      return Number(val || 0).toFixed(2);
    },
    getMovVariant(tipo) {
      if (tipo === "venta") return "light-success";
      if (tipo === "perdido") return "light-danger";
      return "light-info";
    },
    onHidden() {
      this.conductor = null;
      this.meses = [];
      this.movimientos = [];
    },
  },
};
</script>
